import { IonContent, IonLoading, IonPage } from '@ionic/react';
import React, { useEffect, useRef, useState } from 'react';

import {
  Invalid,
  Loading,
  Logo,
  Maintenance,
  Padding,
  PageMeta,
  ProgressBar
} from '@/components/common';
import {
  Confirmation,
  SelectDate,
  SelectDentist,
  SelectPurpose,
  SelectTime,
  SignUp,
  ThankYou,
  Verification
} from '@/components/scheduleFlow';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  ScheduleFlowProvider,
  useScheduleFlow
} from '@/contexts/ScheduleFlowContext';
import { useMaintenanceMode } from '@/hooks';
import { SCHEDULE_FLOW_STEPS } from '@/utils/constants';

import './Schedule.css';

const Schedule: React.FC = () => {
  const {
    loading,
    clinic,
    currentStep,
    setCurrentStep,
    selectedDentist,
    authenticatedPatientId
  } = useScheduleFlow();
  const { t } = useLanguage();
  const isMaintenanceMode = useMaintenanceMode();

  const authenticated = !!authenticatedPatientId;

  const [presentingElement, setPresentingElement] = useState<
    HTMLElement | undefined
  >(undefined);

  const page = useRef<HTMLElement | undefined>(undefined);

  useEffect(() => {
    setPresentingElement(page.current);
  }, [page.current]);

  const showBackButton = [
    SCHEDULE_FLOW_STEPS.SELECT_PURPOSE,
    SCHEDULE_FLOW_STEPS.SELECT_DATE,
    SCHEDULE_FLOW_STEPS.SELECT_TIME,
    SCHEDULE_FLOW_STEPS.SIGN_UP,
    SCHEDULE_FLOW_STEPS.VERIFICATION,
    SCHEDULE_FLOW_STEPS.CONFIRMATION
  ].includes(currentStep);

  const onBack = () => {
    const previousStepIndex =
      Object.values(SCHEDULE_FLOW_STEPS).indexOf(currentStep) -
      (currentStep === SCHEDULE_FLOW_STEPS.CONFIRMATION &&
      (!selectedDentist?.patient_verification_enabled || authenticated)
        ? 2
        : 1);
    const newStep = Object.values(SCHEDULE_FLOW_STEPS)[previousStepIndex];
    if (newStep) setCurrentStep(newStep);
  };

  const renderScheduleFlow = () => {
    const stepComponents: { [key: string]: JSX.Element } =
      selectedDentist?.patient_verification_enabled
        ? {
            [SCHEDULE_FLOW_STEPS.SELECT_DENTIST]: <SelectDentist />,
            [SCHEDULE_FLOW_STEPS.SELECT_PURPOSE]: (
              <SelectPurpose presentingElement={presentingElement} />
            ),
            [SCHEDULE_FLOW_STEPS.SELECT_DATE]: <SelectDate />,
            [SCHEDULE_FLOW_STEPS.SELECT_TIME]: <SelectTime />,
            [SCHEDULE_FLOW_STEPS.SIGN_UP]: (
              <SignUp presentingElement={presentingElement} />
            ),
            [SCHEDULE_FLOW_STEPS.VERIFICATION]: <Verification />,
            [SCHEDULE_FLOW_STEPS.CONFIRMATION]: <Confirmation />,
            [SCHEDULE_FLOW_STEPS.THANK_YOU]: <ThankYou />
          }
        : {
            [SCHEDULE_FLOW_STEPS.SELECT_DENTIST]: <SelectDentist />,
            [SCHEDULE_FLOW_STEPS.SELECT_PURPOSE]: (
              <SelectPurpose presentingElement={presentingElement} />
            ),
            [SCHEDULE_FLOW_STEPS.SELECT_DATE]: <SelectDate />,
            [SCHEDULE_FLOW_STEPS.SELECT_TIME]: <SelectTime />,
            [SCHEDULE_FLOW_STEPS.SIGN_UP]: (
              <SignUp presentingElement={presentingElement} />
            ),
            [SCHEDULE_FLOW_STEPS.CONFIRMATION]: <Confirmation />,
            [SCHEDULE_FLOW_STEPS.THANK_YOU]: <ThankYou />
          };

    const totalSteps = Object.keys(stepComponents).length - 3;
    const currentStepIndex =
      currentStep === SCHEDULE_FLOW_STEPS.THANK_YOU ||
      currentStep === SCHEDULE_FLOW_STEPS.CONFIRMATION
        ? -1
        : Object.keys(stepComponents).indexOf(currentStep) - 1;

    return (
      <>
        <ProgressBar
          totalSteps={totalSteps}
          currentProgress={currentStepIndex}
        />
        {stepComponents[currentStep]}
      </>
    );
  };

  return (
    <IonPage id="schedule-page" ref={page}>
      {clinic && (
        <PageMeta
          title="Book Now at {clinic} - Odonto"
          description="Schedule your appointment at {clinic} easily with Odonto."
          clinic={clinic}
        />
      )}

      <IonContent>
        <Padding>
          <Logo showBackButton={showBackButton} onBack={onBack} />
          {isMaintenanceMode ? (
            <Maintenance />
          ) : clinic ? (
            renderScheduleFlow()
          ) : loading ? (
            <Loading />
          ) : (
            <Invalid />
          )}
        </Padding>
      </IonContent>

      <IonLoading
        isOpen={!!clinic && loading}
        message={t('schedule.loading')}
        spinner="crescent"
      />
    </IonPage>
  );
};

const ScheduleWithProvider: React.FC = () => (
  <ScheduleFlowProvider>
    <Schedule />
  </ScheduleFlowProvider>
);

export default ScheduleWithProvider;
